import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  ErrorState,
  Field,
  Input,
  Select,
  useToast,
} from '@/components/ui';
import { isApiError } from '@/lib/api';
import { useAddPanelReceivableFollowUp } from './api';
import styles from './PanelReceivables.module.css';
import { clinicDate } from '@/lib/clinic-time';

const today = () => clinicDate();

export function LogFollowUpDrawer({
  batchId,
  onClose,
}: {
  batchId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const addFollowUp = useAddPanelReceivableFollowUp();
  const [date, setDate] = useState(today);
  const [channel, setChannel] = useState('email');
  const [note, setNote] = useState('');
  const [nextDate, setNextDate] = useState('');
  const [attempted, setAttempted] = useState(false);
  const apiError = isApiError(addFollowUp.error)
    ? addFollowUp.error
    : undefined;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    const value = note.trim();
    if (!date || !channel || !value) return;
    addFollowUp.mutate(
      {
        batchId,
        date,
        channel,
        note: value,
        nextDate: nextDate || null,
      },
      {
        onSuccess: () => {
          toast.show({
            tone: 'success',
            title: t('panelReceivables.noteAdded'),
          });
          onClose();
        },
      },
    );
  };

  return (
    <Dialog
      open
      title={t('panelReceivables.logFollowUp')}
      onClose={onClose}
      dismissible={!addFollowUp.isPending}
      footer={
        <>
          <Button onClick={onClose} disabled={addFollowUp.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            form="panel-receivable-follow-up"
            variant="primary"
            loading={addFollowUp.isPending}
          >
            {t('panelReceivables.saveFollowUp')}
          </Button>
        </>
      }
    >
      <form
        id="panel-receivable-follow-up"
        className={styles.noteForm}
        onSubmit={submit}
      >
        <Field
          label={t('panelReceivables.followUpDate')}
          error={
            attempted && !date ? t('panelReceivables.dateRequired') : undefined
          }
        >
          <Input
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
          />
        </Field>
        <Field label={t('panelReceivables.channel')}>
          <Select
            value={channel}
            options={[
              { value: 'email', label: t('panelReceivables.channels.email') },
              { value: 'phone', label: t('panelReceivables.channels.phone') },
              { value: 'portal', label: t('panelReceivables.channels.portal') },
              { value: 'other', label: t('panelReceivables.channels.other') },
            ]}
            onChange={(event) => setChannel(event.target.value)}
          />
        </Field>
        <Field
          label={t('panelReceivables.followUpNote')}
          error={
            attempted && !note.trim()
              ? t('panelReceivables.noteRequired')
              : undefined
          }
        >
          <textarea
            className={styles.textarea}
            value={note}
            maxLength={2000}
            onChange={(event) => setNote(event.target.value)}
            placeholder={t('panelReceivables.notePlaceholder')}
          />
        </Field>
        <Field label={t('panelReceivables.nextFollowUp')}>
          <Input
            type="date"
            min={date}
            value={nextDate}
            onChange={(event) => setNextDate(event.target.value)}
          />
        </Field>
        {apiError && <ErrorState error={apiError} />}
      </form>
    </Dialog>
  );
}
